import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, completeNewPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // New password flow state
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await login(email, password);
      console.log('Login result in LoginPage:', result);

      if (result.success) {
        navigate('/dashboard');
      } else if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
        console.log('Setting needsNewPassword to true');
        setNeedsNewPassword(true);
      } else {
        console.log('Login returned no success and no challenge');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      console.error('Error name:', err.name);
      console.error('Error message:', err.message);
      if (err.name === 'NotAuthorizedException') {
        setError('Invalid email or password.');
      } else if (err.name === 'UserNotConfirmedException') {
        setError('Please verify your email first.');
      } else {
        setError(err.message || 'An error occurred during sign in.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match.');
      return;
    }

    // Validate password length
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setIsLoading(true);

    try {
      await completeNewPassword(newPassword);
      navigate('/dashboard');
    } catch (err: any) {
      console.error('New password error:', err);
      if (err.message?.includes('Password did not conform')) {
        setError('Password must include uppercase, lowercase, numbers, and special characters.');
      } else {
        setError(err.message || 'Failed to set new password.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // New password form
  if (needsNewPassword) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="w-full max-w-md">
          <div className="card p-8">
            <div className="mb-8 text-center">
              <img
                src="/logo-hcfs.png"
                alt="HCFS Logo"
                className="mx-auto h-20 w-auto mb-4"
              />
              <h1 className="text-2xl font-bold text-charcoal-700">Set New Password</h1>
              <p className="mt-2 text-sm text-gray-600">
                Please create a new password for your account.
              </p>
            </div>

            {error && (
              <div className="mb-4 rounded-md bg-red-50 p-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <form onSubmit={handleNewPasswordSubmit} className="space-y-6">
              <div>
                <label htmlFor="newPassword" className="label">
                  New Password
                </label>
                <input
                  id="newPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="input mt-1"
                  placeholder="Enter your new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isLoading}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Must be at least 8 characters with uppercase, lowercase, numbers, and special characters.
                </p>
              </div>
              <div>
                <label htmlFor="confirmNewPassword" className="label">
                  Confirm New Password
                </label>
                <input
                  id="confirmNewPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="input mt-1"
                  placeholder="Confirm your new password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  disabled={isLoading}
                />
              </div>
              <button
                type="submit"
                className="btn-primary w-full"
                disabled={isLoading}
              >
                {isLoading ? 'Setting password...' : 'Set Password'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Login form
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-md">
        <div className="card p-8">
          <div className="mb-8 text-center">
            <img
              src="/logo-hcfs.png"
              alt="HCFS Logo"
              className="mx-auto h-24 w-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-charcoal-700">HCFS PeopleMX</h1>
            <p className="mt-2 text-sm text-gray-600">
              People Management System
            </p>
          </div>

          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="label">
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                className="input mt-1"
                placeholder="you@hcfs.org.za"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
              />
            </div>
            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                className="input mt-1"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input type="checkbox" className="h-4 w-4 rounded border-gray-300" />
                <span className="ml-2 text-sm text-gray-600">Remember me</span>
              </label>
              <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-500">
                Forgot password?
              </Link>
            </div>
            <button
              type="submit"
              className="btn-primary w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
